import { DataListComponent } from '../data-list/data-list.component';
import { BoardGroup, DataBoardComponent } from '../data-board/data-board.component';
import { DataPaginationComponent } from '../data-pagination/data-pagination.component';
import { EmptyStateComponent } from '../empty-state/empty-state.component';
import { LoadingStateComponent } from '../loading-state/loading-state.component';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  HostBinding,
  Input,
  Output,
  TemplateRef,
  ViewChild
} from '@angular/core';
import { CommonModule } from '@angular/common';

import {
  CellEditSaveEvent,
  DataTableComponent,
  FilterChip,
  PaginationConfig,
  SortEntry,
  TableColumn
} from '../data-table/data-table.component';
import {
  FilterMenuGroup,
  FilterMenuOption,
  FilterMenuSelection,
  TableToolbarComponent,
  ToolbarPrimaryActionOption,
  ToolbarSortSelection,
  ToolbarViewOption
} from '../table-toolbar/table-toolbar.component';
import { TableMobileCardListComponent } from '../table-mobile-card-list/table-mobile-card-list.component';
import { TablePreferencesService } from '@shared/services/table-preferences.service';

@Component({
  selector: 'app-table-surface',
  standalone: true,
  imports: [
    CommonModule, DataListComponent, DataBoardComponent, DataPaginationComponent, EmptyStateComponent, LoadingStateComponent, MatProgressBarModule,
    TableToolbarComponent,
    DataTableComponent,
    TableMobileCardListComponent
  ],
  templateUrl: './table-surface.component.html',
  styleUrl: './table-surface.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class TableSurfaceComponent<T> {
  @HostBinding('attr.title') readonly hostTitle = null;

  @ViewChild(DataTableComponent) private dataTableRef?: DataTableComponent<T>;
  @ViewChild(TableToolbarComponent) private toolbarRef?: TableToolbarComponent;

  @Input({ required: true }) tableId!: string;
  @Input() columns: TableColumn[] = [];
  @Input() data: T[] | null | undefined = [];
  @Input() isLoading = false;
  @Input() errorMessage = '';
  @Input() noDataMessage = 'No se encontraron registros';
  @Input() paginationConfig?: PaginationConfig;
  @Input() pageSizeOptions?: number[] = [5, 10, 25, 50, 100];
  @Input() showPageSizeSelector = false;
  @Input() tableHeight = '65vh';
  @Input() @HostBinding('class.table-surface-fit') fitContainer = false;
  @Input() enableRowHover = true;
  @Input() enableDragDrop = false;
  @Input() cellTemplate?: TemplateRef<any>;
  @Input() mobileCardTemplate?: TemplateRef<{ $implicit: T; row: T }>;
  @Input() rowDetailTemplate?: TemplateRef<any>;
  @Input() expandedRow: T | null = null;
  @Input() headerTemplate?: TemplateRef<any>;
  @Input() initialSort?: { sortBy: string; direction: 'asc' | 'desc' };
  @Input() enableHeaderSort = true;
  @Input() serverSideSort = false;
  @Input() multiSort = false;
  @Input() editableColumns: string[] = [];
  @Input() savingCell?: { row: any; column: string };

  @Input() title?: string;
  @Input() titleIcon?: string;
  @Input() titleAriaLabel?: string;
  @Input() titleInfoTooltip?: string;
  @Input() activeFilters: FilterChip[] = [];
  @Input() enableSearch = false;
  @Input() hasQuickFilters = false;
  @Input() searchPlaceholder = 'Buscar...';
  @Input() enableColumnVisibility = false;
  @Input() enableDensity = false;
  @Input() enableExport = false;
  @Input() enableFilter = false;
  @Input() enableClearFilters = false;
  @Input() enableSort = false;
  @Input() exportFileName?: string;
  @Input() sortState?: ToolbarSortSelection | null;
  @Input() sortHiddenColumnKeys: string[] = [];
  @Input() filterMenuTitle = 'Filtros';
  @Input() filterOptions: FilterMenuOption[] = [];
  @Input() filterGroups: FilterMenuGroup[] = [];
  @Input() primaryActionLabel?: string;
  @Input() primaryActionMobileLabel?: string;
  @Input() primaryActionOptions: ToolbarPrimaryActionOption[] = [];
  @Input() viewOptions: ToolbarViewOption[] = [];
  @Input() activeView = 'table';
  @Input() listItemTemplate?: TemplateRef<any>;
  @Input() boardCardTemplate?: TemplateRef<any>;
  @Input() boardGroups: BoardGroup[] = [];
  @Input() boardGroupBy = '';
  @Input() enableBoardDragDrop = false;
  @Input() customViewTemplate?: TemplateRef<any>;
  @Input('readonly') readOnly = false;

  density: 'compact' | 'normal' | 'comfortable' = 'normal';

  @Output() pageChange = new EventEmitter<{ page: number }>();
  @Output() pageSizeChange = new EventEmitter<{ pageSize: number }>();
  @Output() sortChange = new EventEmitter<{ sortBy: string; direction: 'asc' | 'desc' }>();
  @Output() sortStateChange = new EventEmitter<SortEntry[]>();
  @Output() rowClick = new EventEmitter<T>();
  @Output() rowReorder = new EventEmitter<{ previousIndex: number; currentIndex: number; data: T[] }>();
  @Output() cellEditSave = new EventEmitter<CellEditSaveEvent<T>>();

  @Output() searchChange = new EventEmitter<string>();
  @Output() searchInput = new EventEmitter<string>();
  @Output() filterRemove = new EventEmitter<string>();
  @Output() filterOptionSelect = new EventEmitter<FilterMenuSelection>();
  @Output() clearFiltersClick = new EventEmitter<void>();
  @Output() toolbarSortChange = new EventEmitter<ToolbarSortSelection>();
  @Output() boardGroupChange = new EventEmitter<{ row: T; group: string }>();
  @Output() columnVisibilityChange = new EventEmitter<{ key: string; visible: boolean }>();
  @Output() densityChange = new EventEmitter<'compact' | 'normal' | 'comfortable'>();
  @Output() exportClick = new EventEmitter<void>();
  @Output() primaryActionClick = new EventEmitter<void>();
  @Output() primaryActionOptionClick = new EventEmitter<ToolbarPrimaryActionOption>();
  @Output() viewChange = new EventEmitter<string>();

  constructor(
    private readonly cdr: ChangeDetectorRef,
    private readonly tablePrefs: TablePreferencesService
  ) {}

  get resolvedPrimaryActionLabel(): string | undefined {
    if (!this.hasPrimaryActionHandler) {
      return undefined;
    }

    return this.primaryActionLabel?.trim() || 'Nuevo';
  }

  get resolvedPrimaryActionMobileLabel(): string | undefined {
    if (!this.hasPrimaryActionHandler) {
      return undefined;
    }

    return this.primaryActionMobileLabel;
  }

  get mobileColumns(): TableColumn[] {
    return this.dataTableRef?.renderColumns ?? this.columns.filter(column => column.visible !== false);
  }

  get resolvedViewOptions(): ToolbarViewOption[] {
    return this.viewOptions.length > 0
      ? this.viewOptions
      : [{ value: 'table', icon: 'table_rows', label: 'Tabla' }];
  }

  get shouldShowTable(): boolean {
    return this.activeView === 'table' || (!this.customViewTemplate && !['list', 'board'].includes(this.activeView));
  }

  get shouldShowCollection(): boolean { return !this.customViewTemplate && ['list', 'board'].includes(this.activeView); }

  get shouldShowCustomView(): boolean {
    return this.activeView !== 'table' && !!this.customViewTemplate;
  }

  onToolbarColumnVisibility(event: { key: string; visible: boolean }): void {
    this.dataTableRef?.setColumnVisibility(event.key, event.visible);
    this.columnVisibilityChange.emit(event);
  }

  onToolbarDensityChange(density: 'compact' | 'normal' | 'comfortable'): void {
    this.density = density;
    this.dataTableRef?.setDensity(density);
    this.densityChange.emit(density);
    this.cdr.markForCheck();
  }

  onToolbarSortChange(event: ToolbarSortSelection): void {
    this.tablePrefs.patch(this.tableId, { sort: event });
    this.dataTableRef?.setSort(event);
    this.toolbarSortChange.emit(event);
  }

  onDataTableSortChange(event: { sortBy: string; direction: 'asc' | 'desc' }): void {
    this.tablePrefs.patch(this.tableId, { sort: event });
    if (this.sortChange.observed) {
      this.sortChange.emit(event);
      return;
    }

    this.toolbarSortChange.emit(event);
  }

  onExport(): void {
    if (this.exportFileName?.trim()) {
      this.dataTableRef?.exportToCsv(this.exportFileName.trim());
    } else {
      this.dataTableRef?.exportToCsv();
    }
    this.exportClick.emit();
  }

  clearSearch(): void {
    this.toolbarRef?.clearSearch();
  }

  private get hasPrimaryActionHandler(): boolean {
    return this.primaryActionClick.observed || this.primaryActionOptionClick.observed;
  }
}
